import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Loading, Toast, useAsync } from '../components/common';
import type { Principal } from '../types';

export default function UsersPage({ principal }: { principal: Principal }) {
  const { data, error, loading, reload } = useAsync(() => api.listUsers(), []);
  const month = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const audit = useAsync(() => api.listAudit(month), [month]);
  const [toast, setToast] = useState<{ message: string; tone: 'info' | 'error' } | null>(null);
  const [createdSecret, setCreatedSecret] = useState<{
    email: string;
    uri: string;
    secret: string;
  } | null>(null);

  const [form, setForm] = useState({
    email: '',
    display_name: '',
    password: '',
    role: 'viewer' as 'admin' | 'viewer',
    confirm_mfa: true,
  });

  if (principal.role !== 'admin') {
    return (
      <div className="callout warn">
        User management is admin-only. Your role is <code>{principal.role}</code>.
      </div>
    );
  }

  if (loading || !data) return <Loading error={error} />;

  async function create() {
    try {
      const result = await api.createUser(form);
      setCreatedSecret({
        email: result.email,
        uri: result.provisioning_uri,
        secret: result.totp_secret,
      });
      setForm({
        email: '',
        display_name: '',
        password: '',
        role: 'viewer',
        confirm_mfa: true,
      });
      reload();
      audit.reload();
      setToast({ message: `Created ${result.email}. Copy the MFA secret now.`, tone: 'info' });
    } catch (err) {
      setToast({ message: (err as Error).message, tone: 'error' });
    }
  }

  async function patch(email: string, body: Parameters<typeof api.patchUser>[1]) {
    try {
      await api.patchUser(email, body);
      reload();
      audit.reload();
      setToast({ message: `Updated ${email}.`, tone: 'info' });
    } catch (err) {
      setToast({ message: (err as Error).message, tone: 'error' });
    }
  }

  async function confirmMfa(email: string) {
    try {
      await api.confirmMfa(email);
      reload();
      setToast({ message: `MFA confirmed for ${email}.`, tone: 'info' });
    } catch (err) {
      setToast({ message: (err as Error).message, tone: 'error' });
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <p>
            Admin-only access control. There is no partner login and no self-service signup —
            every account is provisioned here with password + TOTP.
          </p>
        </div>
      </div>

      {createdSecret && (
        <div className="callout warn">
          <strong>One-time MFA material for {createdSecret.email}</strong>
          <div className="small" style={{ marginTop: 8 }}>
            Store this now — it is not shown again.
          </div>
          <div className="mono" style={{ marginTop: 8, wordBreak: 'break-all' }}>
            secret: {createdSecret.secret}
          </div>
          <div className="mono small" style={{ marginTop: 6, wordBreak: 'break-all' }}>
            {createdSecret.uri}
          </div>
          <div className="small muted" style={{ marginTop: 8 }}>
            Code without Authenticator:{' '}
            <code>oathtool --totp -b {createdSecret.secret}</code>
          </div>
          <button style={{ marginTop: 10 }} onClick={() => setCreatedSecret(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="card">
        <h2>Create user</h2>
        <div className="row">
          <label className="field">
            <span>Email</span>
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="name@sbfloans.com"
            />
          </label>
          <label className="field">
            <span>Display name</span>
            <input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Password (min 12)</span>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Role</span>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'viewer' })}
            >
              <option value="viewer">viewer</option>
              <option value="admin">admin</option>
            </select>
          </label>
        </div>
        <label className="inline">
          <input
            type="checkbox"
            checked={form.confirm_mfa}
            onChange={(e) => setForm({ ...form, confirm_mfa: e.target.checked })}
          />
          Mark MFA enrolled immediately (operator has captured the secret)
        </label>
        <div className="actions" style={{ marginTop: 12 }}>
          <button
            className="primary"
            onClick={create}
            disabled={!form.email || form.password.length < 12}
          >
            Create user
          </button>
        </div>
      </div>

      <div className="card">
        <h2>
          Accounts
          <span className="hint">{data.length} users</span>
        </h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>MFA</th>
                <th>Last login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((user) => (
                <tr key={user.email}>
                  <td>
                    <div>{user.display_name}</div>
                    <div className="small muted mono">{user.email}</div>
                  </td>
                  <td>
                    <select
                      value={user.role}
                      disabled={user.email === principal.email}
                      onChange={(e) =>
                        patch(user.email, { role: e.target.value as 'admin' | 'viewer' })
                      }
                    >
                      <option value="viewer">viewer</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    {user.active ? <Badge tone="ok">active</Badge> : <Badge tone="danger">disabled</Badge>}
                    {user.locked_until && (
                      <div className="small">
                        <Badge tone="warn">locked until {user.locked_until}</Badge>
                      </div>
                    )}
                  </td>
                  <td>
                    {user.mfa_enrolled ? (
                      <Badge tone="ok">enrolled</Badge>
                    ) : (
                      <Badge tone="warn">pending</Badge>
                    )}
                  </td>
                  <td className="small mono">{user.last_login_at ?? '—'}</td>
                  <td>
                    <div className="actions">
                      {!user.mfa_enrolled && (
                        <button onClick={() => confirmMfa(user.email)}>Confirm MFA</button>
                      )}
                      {user.locked_until && (
                        <button onClick={() => patch(user.email, { unlock: true })}>Unlock</button>
                      )}
                      <button
                        className={user.active ? 'danger' : 'primary'}
                        disabled={user.email === principal.email}
                        onClick={() => patch(user.email, { active: !user.active })}
                      >
                        {user.active ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>
          Audit log
          <span className="hint">{month}</span>
        </h2>
        {audit.loading || !audit.data ? (
          <Loading error={audit.error} />
        ) : audit.data.length === 0 ? (
          <div className="callout">No audit events this month yet.</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Entity</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.map((entry) => (
                  <tr key={entry.entry_id}>
                    <td className="mono small">{entry.at}</td>
                    <td className="mono small">{entry.actor}</td>
                    <td>
                      <Badge tone="info">{entry.action}</Badge>
                    </td>
                    <td className="small">
                      {entry.entity_type} <code>{entry.entity_id}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && <Toast {...toast} onDone={() => setToast(null)} />}
    </>
  );
}
